import { useContext } from 'react';

import { TourKeys } from '@cast/constants';
import { ConditionalWrapper, Typography } from '@cast/design-system';
import { WorkloadScalingPolicy } from '@cast/types';

import { useIsTourActive } from 'core/tour-v2/hooks/useIsTourActive';
import { WarningTooltip } from 'features/cluster/workload-optimization/_components/WarningTooltip';
import { OptimizationWorkloadsListContext } from 'features/cluster/workload-optimization/workloads-list/context';
import { PreparedWorkload } from 'features/workload-optimization';

import { useAssignScalingPolicyDialog } from '../../../_components/AssignPolicyDialog';
import { ScalingPolicyAutocomplete } from '../../../_components/ScalingPolicyAutocomplete';
import { useAutoAssignmentToggle } from '../../../_hooks/useAutoAssignmentToggle';

type ScalingPolicyCellProps = {
  disabled: boolean;
  workload: PreparedWorkload;
  canEdit: boolean;
  policies: WorkloadScalingPolicy[];
  isWoopDemoMode?: boolean;
};

export const ScalingPolicyCell = ({
  disabled: _disabled,
  workload,
  canEdit,
  policies,
  isWoopDemoMode = false,
}: ScalingPolicyCellProps) => {
  const { openAssignPolicyModal } = useAssignScalingPolicyDialog();
  const isOpTourActive = useIsTourActive(TourKeys.OpinionatedPlatform);
  const { isNamespaceWritable } = useContext(OptimizationWorkloadsListContext);
  const isReadOnlyNamespace = !isNamespaceWritable(workload.namespace);

  const disabled =
    !canEdit ||
    isWoopDemoMode ||
    isOpTourActive ||
    workload.verticalScalingPolicy?.isCastware ||
    workload.isManagedByAnnotations;

  // Auto-assignment toggle hook
  const {
    enableAutoAssignment,
    disableAutoAssignment,
    isAutoAssignmentEnabled,
  } = useAutoAssignmentToggle({ workload });

  const handlePolicyChange = (
    newSelectedPolicy: typeof workload.verticalScalingPolicy
  ) => {
    if (!newSelectedPolicy) return;

    // If auto-assignment is enabled, show disable confirmation
    if (isAutoAssignmentEnabled) {
      disableAutoAssignment(newSelectedPolicy);
    } else {
      // Normal flow: show the assign policy modal
      openAssignPolicyModal({
        workload,
        newScalingPolicy: newSelectedPolicy,
      });
    }
  };

  // Disable header click when any of the disabled conditions are true
  const canEnableAutoAssignment =
    canEdit &&
    !isWoopDemoMode &&
    !isOpTourActive &&
    !workload.verticalScalingPolicy?.isCastware &&
    !workload.isManagedByAnnotations;

  if (isReadOnlyNamespace) {
    return (
      <Typography variant="P12R" color="blue.500" noWrap>
        {workload.verticalScalingPolicy?.name ?? '—'}
      </Typography>
    );
  }

  return (
    <ConditionalWrapper
      condition={
        !_disabled &&
        (workload.verticalScalingPolicy?.isCastware ||
          workload.isManagedByAnnotations)
      }
      wrapper={(child) => (
        <WarningTooltip
          title={`Cannot switch vertical scaling policy because the workload is ${
            workload.isManagedByAnnotations
              ? 'managed by annotations'
              : 'assigned to castware policy'
          }.`}
        >
          {child}
        </WarningTooltip>
      )}
    >
      <div>
        <ScalingPolicyAutocomplete
          workload={workload}
          policies={policies}
          disabled={disabled}
          isNamespaceWritableForWorkload={!isReadOnlyNamespace}
          onChange={handlePolicyChange}
          onHeaderClick={
            canEnableAutoAssignment ? enableAutoAssignment : undefined
          }
          popoverProps={{
            anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
            transformOrigin: { vertical: 'top', horizontal: 'right' },
          }}
          testId="vertical-scaling-policy-select"
        />
      </div>
    </ConditionalWrapper>
  );
};
